import { useCallback, useEffect, useState } from 'react';
import { Box, Button, InputAdornment, MenuItem, TextField, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { CommonDatagrid } from '..';
import { OrderRerunsService } from '../../../api/services/orderRerunsService';
import {
  setOrderReruns,
  setPagination,
  setSelectedRerun,
} from '../../../store/Reducers/OrderRerunsSlice';
import { showNotification } from '../../../store/Reducers/NotificationSnackbarSlice';
import { IOrderRerun } from '../../../types/api/response/orderrerunsresponse';
import { IRootState } from '../../../types/store';
import { getOrderRerunsColumns } from './columns';
import { OrderRerunDetailDialog } from './OrderRerunDetailDialog';

const SOURCES = ['ADMIN_PANEL', 'SUPERPRO'];

export const OrderRerunsDatagrid = () => {
  const dispatch = useDispatch();
  const { reruns, totalElements, pagination, selectedRerun } = useSelector(
    (state: IRootState) => state.orderReruns,
  );

  const [source, setSource] = useState('');
  const [search, setSearch] = useState('');
  const [detailOpen, setDetailOpen] = useState(false);

  const columns = getOrderRerunsColumns();

  const fetchReruns = useCallback(
    async (page = 0, size = pagination.size, sourceVal = source, searchVal = search) => {
      try {
        const res = await OrderRerunsService.searchRerunHistory({
          ...(sourceVal ? { source: sourceVal } : {}),
          ...(searchVal ? { search: searchVal } : {}),
          page,
          size,
        });
        dispatch(setOrderReruns({ content: res.content, totalElements: res.page.totalElements }));
        dispatch(setPagination({ page: res.page.number, size: res.page.size }));
      } catch {
        dispatch(showNotification({ message: 'Failed to fetch order reruns.', severity: 'error' }));
      }
    },
    [dispatch, pagination.size, source, search],
  );

  useEffect(() => {
    fetchReruns(0, pagination.size, '', '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDetail = (rerun: IOrderRerun) => {
    dispatch(setSelectedRerun(rerun));
    setDetailOpen(true);
  };

  const filterBar = (
    <Box display="flex" flexWrap="wrap" gap={2} alignItems="flex-end" mb={2}>
      <TextField
        select
        label="Source"
        size="small"
        value={source}
        onChange={(e) => setSource(e.target.value)}
        sx={{ minWidth: 200 }}
      >
        <MenuItem value="">All sources</MenuItem>
        {SOURCES.map((s) => (
          <MenuItem key={s} value={s}>
            {s === 'ADMIN_PANEL' ? 'Admin Panel' : s}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        size="small"
        label="Order"
        placeholder="Search by order number or DS order number..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ minWidth: 320 }}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title="Search by order number (ORD-) or DS order number (SO-)" arrow>
                  <InfoOutlinedIcon
                    fontSize="small"
                    sx={{ color: 'text.secondary', cursor: 'help' }}
                  />
                </Tooltip>
              </InputAdornment>
            ),
          },
        }}
      />
      <Button
        variant="contained"
        onClick={() => fetchReruns(0, pagination.size, source, search)}
        sx={{ fontSize: 12, fontWeight: 600, height: 40 }}
      >
        Apply Filters
      </Button>
      <Button
        variant="outlined"
        onClick={() => {
          setSource('');
          setSearch('');
          fetchReruns(0, pagination.size, '', '');
        }}
        sx={{ fontSize: 12, height: 40 }}
      >
        Clear
      </Button>
    </Box>
  );

  return (
    <>
      <CommonDatagrid
        header={filterBar}
        data={reruns}
        columns={columns}
        rowId="id"
        serverPagination
        page={pagination.page}
        pageSize={pagination.size}
        rowCount={totalElements}
        onPageChange={(p) => fetchReruns(p, pagination.size)}
        onPageSizeChange={(s) => fetchReruns(0, s)}
        onRowClick={({ row }) => openDetail(row as IOrderRerun)}
        isCellEditable={() => false}
      />
      <OrderRerunDetailDialog
        rerun={selectedRerun}
        isOpen={detailOpen}
        onClose={() => {
          setDetailOpen(false);
          dispatch(setSelectedRerun(null));
        }}
      />
    </>
  );
};
